import { runAzureLiveTranscribe } from "../api/runAzureLiveTranscribe";
import { Utterance } from "../types/utterance";
import type { Transcriber, TranscriptionState } from "./Transcriber";

export class MicTranscriber implements Transcriber {
  private state: TranscriptionState = "idle";
  private stopFn: null | (() => Promise<void>) = null;
  private utteranceSubs = new Set<(u: Utterance) => void>();
  private stateSubs = new Set<(s: TranscriptionState) => void>();
  private errorSubs = new Set<(e: Error) => void>();

  getState() {
    return this.state;
  }
  private setState(s: TranscriptionState) {
    this.state = s;
    this.stateSubs.forEach((fn) => fn(s));
  }

  onUtterance(cb: (u: Utterance) => void) {
    this.utteranceSubs.add(cb);
    return () => this.utteranceSubs.delete(cb);
  }
  onState(cb: (s: TranscriptionState) => void) {
    this.stateSubs.add(cb);
    return () => this.stateSubs.delete(cb);
  }
  onError(cb: (e: Error) => void) {
    this.errorSubs.add(cb);
    return () => this.errorSubs.delete(cb);
  }

  async start(opts: { languages: string[] }) {
    if (this.stopFn) return; // already running
    this.setState("connecting");
    try {
      // Early env var check surfaces clear error instead of silent reset
      const key = import.meta.env.VITE_SPEECH_KEY;
      const region = import.meta.env.VITE_SPEECH_REGION;
      if (!key || !region) {
        throw new Error(
          "Missing Speech configuration (VITE_SPEECH_KEY / VITE_SPEECH_REGION)"
        );
      }
      this.stopFn = await runAzureLiveTranscribe({
        langs: opts.languages,
        onUtterance: (u) => this.utteranceSubs.forEach((fn) => fn(u)),
        onComplete: () => {
          /* no-op here; UI decides */
        },
        onError: (e) => {
          this.setState("error");
          this.errorSubs.forEach((fn) => fn(e));
        },
      });
      this.setState("listening");
    } catch (e: any) {
      this.setState("error");
      this.errorSubs.forEach((fn) => fn(e));
      throw e;
    }
  }

  async stop() {
    try {
      await this.stopFn?.();
    } finally {
      this.stopFn = null;
      this.setState("stopped");
    }
  }
}
